import { useChat } from "@/chat/ChatContext";
import { MessageList } from "@/chat/MessageList";
import { ChatInput } from "@/chat/ChatInput";
import { useEffect, useState } from "react";
import { useAuth } from "@/auth/useAuth";
import { ChatOnboardingTour } from "@/onboarding/ChatOnboardingTour";
import { HelpCircle, Plus, RefreshCw, X } from "lucide-react";
import { ChatWizard } from "@/chat/wizard/ChatWizard";
import { useAnalisiChat } from "@/components/analisi/useAnalisiChat";
import { RiquadroSpesaVendita, StrisciaSpesaVendita } from "@/components/analisi/RiquadroSpesaVendita";

export function ChatPage() {
  const {
    messages,
    sendMessage,
    sendBatch,
    isConnected,
    isLoading,
    ensureConnected,
    wizardCompleted,
    showRestartCta,
    analisiIds,
    analisiAzzera,
    startFromWizard,
    requestNewWizard,
    skipWizard,
  } = useChat();
  const { hasRole } = useAuth();
  const isStaff = hasRole("affiliato") || hasRole("operatore");
  const analisi = useAnalisiChat(analisiIds, { ripristina: isStaff, azzera: analisiAzzera });
  const [replayNonce, setReplayNonce] = useState(0);
  const [showNewConsultConfirm, setShowNewConsultConfirm] = useState(false);
  const [dismissedCta, setDismissedCta] = useState(false);

  // Connect WS when entering fullscreen (independent of bubble isOpen)
  useEffect(() => {
    ensureConnected();
  }, [ensureConnected]);

  // Reset dismiss state whenever a new restart CTA arrives
  useEffect(() => {
    if (showRestartCta) setDismissedCta(false);
  }, [showRestartCta]);

  // Staff: show wizard gate if wizard not yet completed
  if (isStaff && !wizardCompleted) {
    return (
      <div className="h-[calc(100vh-8rem)] overflow-y-auto">
        <ChatWizard
          hasActiveSession={isConnected || messages.length > 0}
          onResume={skipWizard}
          onSkip={skipWizard}
          onSubmit={startFromWizard}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-8rem)]">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span
            className={`w-2.5 h-2.5 rounded-full ${isConnected ? "bg-green-500" : "bg-red-500"}`}
          />
          <span className="text-sm text-muted-foreground">
            {isConnected ? "Connesso" : "Disconnesso"}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {isStaff && (
            <>
              {showNewConsultConfirm ? (
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-muted-foreground">Aprire nuova consulenza?</span>
                  <button
                    type="button"
                    onClick={() => {
                      setShowNewConsultConfirm(false);
                      requestNewWizard();
                    }}
                    className="text-white bg-[#4B9B2B] hover:bg-[#3a7a21] rounded px-2.5 py-1 font-semibold transition-colors"
                  >
                    Sì, nuova
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowNewConsultConfirm(false)}
                    className="text-muted-foreground hover:text-foreground rounded px-2 py-1 transition-colors"
                  >
                    Annulla
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowNewConsultConfirm(true)}
                  data-tour="chat-nuova"
                  className="flex items-center gap-1.5 text-xs font-semibold text-white bg-[#4B9B2B] hover:bg-[#3a7a21] rounded-md px-3 py-1.5 transition-colors"
                >
                  <Plus size={13} />
                  <span>Nuova consulenza</span>
                </button>
              )}
            </>
          )}
          {isStaff && !showNewConsultConfirm && (
            <button
              type="button"
              onClick={() => setReplayNonce((n) => n + 1)}
              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
              title="Rivedi guida"
            >
              <HelpCircle size={14} />
              <span>Rivedi guida</span>
            </button>
          )}
        </div>
      </div>

      {/* Restart CTA banner */}
      {isStaff && showRestartCta && !dismissedCta && (
        <div className="mb-3 flex items-center justify-between gap-3 rounded-lg border border-[#4B9B2B]/30 bg-[#4B9B2B]/8 px-4 py-3">
          <div className="flex items-center gap-2 text-sm">
            <RefreshCw size={15} className="text-[#4B9B2B] flex-none" />
            <span className="text-foreground">
              Pratica completata.{" "}
              <button
                type="button"
                onClick={() => requestNewWizard()}
                className="font-semibold text-[#4B9B2B] hover:underline"
              >
                Avvia nuova consulenza
              </button>{" "}
              per un nuovo cliente.
            </span>
          </div>
          <button
            type="button"
            onClick={() => setDismissedCta(true)}
            className="text-muted-foreground hover:text-foreground flex-none"
            title="Chiudi"
          >
            <X size={14} />
          </button>
        </div>
      )}

      <div
        data-tour="chat-container"
        className={`flex-1 min-h-0 bg-card rounded-xl border border-border overflow-hidden flex flex-col ${
          analisi.length > 0 ? "lg:grid lg:grid-cols-[1fr_300px] lg:grid-rows-[minmax(0,1fr)]" : ""
        }`}
      >
        <div className="flex-1 min-h-0 min-w-0 flex flex-col">
          <StrisciaSpesaVendita voci={analisi} />
          <MessageList messages={messages} isLoading={isLoading} />
          <ChatInput onSend={sendMessage} onSendBatch={sendBatch} />
        </div>
        <RiquadroSpesaVendita voci={analisi} />
      </div>
      <ChatOnboardingTour replayNonce={replayNonce} />
    </div>
  );
}
